<template>
  <el-card v-if="viewModel" class="card linkInfoSection">
    <div slot="header">
      <strong class="linkInfoSection__title">{{ viewModel.sectionTitle }}</strong>
    </div>

    <div class="linkInfoWorkout">
      <div
        v-if="stripFieldRows.length"
        class="linkInfoWorkout__meta linkInfoWorkout__section"
        :class="[metaStripClass, { 'linkInfoWorkout__meta--multiRow': stripFieldRows.length > 1 }]"
      >
        <template v-for="(row, rowIndex) in stripFieldRows">
          <div
            v-for="field in row"
            :key="field.label + '-' + rowIndex"
            class="linkInfoWorkout__field"
          >
            <div class="linkInfoWorkout__label link-font-mono">{{ field.label }}</div>
            <div
              class="linkInfoWorkout__value"
              :class="{ 'linkInfoWorkout__value--warn': field.warn }"
            >
              {{ field.value }}
            </div>
          </div>
        </template>
      </div>

      <div
        v-for="field in viewModel.blockFields || []"
        :key="field.label"
        class="linkInfoWorkout__block linkInfoWorkout__section"
      >
        <div class="linkInfoWorkout__field">
          <div class="linkInfoWorkout__label link-font-mono">{{ field.label }}</div>
          <div
            class="linkInfoWorkout__value"
            :class="{ 'linkInfoWorkout__value--warn': field.warn }"
          >
            {{ field.value }}
          </div>
          <p v-if="field.hint" class="linkInfoWorkout__hint">{{ field.hint }}</p>
        </div>
      </div>

      <div
        v-if="viewModel.bindings && viewModel.bindings.length"
        class="linkInfoWorkout__bindings linkInfoWorkout__section"
      >
        <div
          v-for="zone in viewModel.bindings"
          :key="zone.id"
          class="linkInfoWorkout__zone"
          :class="'linkInfoWorkout__zone--' + zone.id"
        >
          <div class="linkInfoWorkout__zoneHead">
            <span class="linkInfoWorkout__zoneBadge" aria-hidden="true">{{ zoneBadge(zone.id) }}</span>
            <div class="linkInfoWorkout__zoneHeadText">
              <div class="linkInfoWorkout__zoneTitle">{{ zone.label }}</div>
              <p class="linkInfoWorkout__zoneDesc">{{ zoneDescription(zone.id) }}</p>
            </div>
            <span v-if="zone.items && zone.items.length" class="linkInfoWorkout__zoneCount link-font-mono">
              共 {{ zone.items.length }} 条
            </span>
          </div>

          <div class="linkInfoWorkout__zoneBody">
            <div v-if="zone.items && zone.items.length" class="linkInfoWorkout__bindingList">
              <div
                v-for="(item, index) in zone.items"
                :key="zone.id + '-item-' + item.key + '-' + index"
                class="linkInfoWorkout__bindingCard"
              >
                <div class="linkInfoWorkout__bindingTitle">
                  <span v-if="item.order" class="linkInfoWorkout__bindingOrder">#{{ item.order }}</span>
                  {{ item.title }}
                </div>
                <div
                  v-if="bindingPrimaryLines(zone.id, item).length"
                  class="linkInfoWorkout__bindingGrid"
                  :class="'linkInfoWorkout__bindingGrid--' + zone.id"
                >
                  <div
                    v-for="line in bindingPrimaryLines(zone.id, item)"
                    :key="'line-' + line.key"
                    class="linkInfoWorkout__bindingCell"
                  >
                    <div class="linkInfoWorkout__label link-font-mono">{{ line.key }}</div>
                    <div
                      class="linkInfoWorkout__bindingMetaVal"
                      :class="{ 'linkInfoWorkout__bindingMetaVal--warn': line.muted }"
                    >
                      {{ line.value }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="bindingConfigLines(zone.id, item).length"
                  class="linkInfoWorkout__bindingGrid linkInfoWorkout__bindingGrid--config"
                  :class="'linkInfoWorkout__bindingGrid--' + zone.id"
                >
                  <div
                    v-for="line in bindingConfigLines(zone.id, item)"
                    :key="'cfg-' + line.key"
                    class="linkInfoWorkout__bindingCell"
                  >
                    <div class="linkInfoWorkout__label link-font-mono">{{ line.key }}</div>
                    <div class="linkInfoWorkout__bindingMetaVal">{{ line.value }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="linkInfoWorkout__zoneEmpty">
              {{ zone.emptyText }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-card>
</template>

<script>
import { buildWorkoutExtaView, enrichWorkoutCollectionExtaView } from './genericResourceInfoHelper'
import { BINDING_ZONE_UI } from './form/basicForms/workoutCollectionBindingModel'

/**
 * HomeWorkout 查看页：合集 binding 按 resource_id 拉子资源展示；落库不含快照字段。
 */
export default {
  name: 'GenericResourceWorkoutReadonly',
  props: {
    info: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      enrichedViewModel: null,
      enrichRequestSeq: 0,
    }
  },
  computed: {
    baseViewModel() {
      const assetType = this.info && this.info.assetType
      const jsonConfig = (this.info && this.info.jsonConfig) || {}
      return buildWorkoutExtaView(assetType, jsonConfig)
    },
    viewModel() {
      return this.enrichedViewModel || this.baseViewModel
    },
    stripFieldRows() {
      const vm = this.viewModel
      if (!vm) {
        return []
      }
      if (Array.isArray(vm.stripFieldRows) && vm.stripFieldRows.length) {
        return vm.stripFieldRows
      }
      if (Array.isArray(vm.stripFields) && vm.stripFields.length) {
        return [vm.stripFields]
      }
      return []
    },
    metaStripClass() {
      const variant = (this.viewModel && this.viewModel.stripVariant) || 'default'
      return `linkInfoWorkout__meta--${variant}`
    },
  },
  watch: {
    info: {
      immediate: true,
      deep: true,
      handler() {
        this.loadEnrichedViewModel()
      },
    },
  },
  methods: {
    zoneBadge(zoneId) {
      const ui = BINDING_ZONE_UI[zoneId]
      return (ui && ui.badge) || '?'
    },
    zoneDescription(zoneId) {
      const ui = BINDING_ZONE_UI[zoneId]
      return (ui && ui.description) || ''
    },
    /** 音乐区：子资源 ref 与合集内 loop 同一行；动作区 ref 单独一行 */
    bindingPrimaryLines(zoneId, item) {
      const refLines = (item && item.refLines) || []
      if (zoneId === 'music') {
        return [...refLines, ...((item && item.configLines) || [])]
      }
      return refLines
    },
    /** 动作区编排参数单独第二行；音乐区 config 已并入 primary */
    bindingConfigLines(zoneId, item) {
      if (zoneId === 'music') {
        return []
      }
      return (item && item.configLines) || []
    },
    async loadEnrichedViewModel() {
      const base = this.baseViewModel
      if (!base || base.kind !== 'collection') {
        this.enrichedViewModel = null
        return
      }
      const seq = ++this.enrichRequestSeq
      try {
        const jsonConfig = (this.info && this.info.jsonConfig) || {}
        const enriched = await enrichWorkoutCollectionExtaView(base, jsonConfig)
        if (seq !== this.enrichRequestSeq) {
          return
        }
        this.enrichedViewModel = enriched
      } catch (e) {
        if (seq !== this.enrichRequestSeq) {
          return
        }
        // 子资源拉取失败时仍展示合集 exta 与编排参数
        this.enrichedViewModel = null
      }
    },
  },
}
</script>

<style scoped>
@import './linkTypography.css';

.linkInfoWorkout__section + .linkInfoWorkout__section {
  margin-top: 0;
  padding-top: 16px;
  border-top: 1px solid var(--link-line, #eee9e2);
}

.linkInfoWorkout__meta {
  display: grid;
  gap: 16px 24px;
  margin: 0;
  padding: 0;
}

.linkInfoWorkout__meta--action,
.linkInfoWorkout__meta--voice,
.linkInfoWorkout__meta--music,
.linkInfoWorkout__meta--collection {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

/* 合集两行 strip：同一 grid 内换行，避免双容器 + 负 margin 把上下行挤在一起 */
.linkInfoWorkout__meta--multiRow {
  row-gap: 20px;
  column-gap: 24px;
}

.linkInfoWorkout__meta--nameLoop {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.linkInfoWorkout__bindings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
}

.linkInfoWorkout__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

/* 三区独立卡片：与编辑页 workoutZone 同构，查看页只读无交互 */
.linkInfoWorkout__zone {
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 12px;
  background: var(--link-card-surface, #fff);
  box-shadow: 0 1px 2px rgba(28, 25, 23, 0.04);
  overflow: hidden;
}

.linkInfoWorkout__zoneHead {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--link-field-bg, #faf8f5);
  border-bottom: 1px solid var(--link-line-strong, #e5dfd6);
}

.linkInfoWorkout__zone--actions .linkInfoWorkout__zoneHead {
  border-left: 3px solid var(--link-accent, #a67c52);
}

.linkInfoWorkout__zone--music .linkInfoWorkout__zoneHead {
  border-left: 3px solid #7c9eb2;
}

.linkInfoWorkout__zone--voices .linkInfoWorkout__zoneHead {
  border-left: 3px solid #8f9a6d;
}

.linkInfoWorkout__zoneBadge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--link-accent-bg, #f5efe3);
  color: var(--link-accent, #a67c52);
  font-size: var(--link-text-xs, 12px);
  font-weight: var(--link-font-semibold);
}

.linkInfoWorkout__zone--music .linkInfoWorkout__zoneBadge {
  background: #eef3f6;
  color: #5a7a8f;
}

.linkInfoWorkout__zone--voices .linkInfoWorkout__zoneBadge {
  background: #f2f4eb;
  color: #6b7550;
}

.linkInfoWorkout__zoneHeadText {
  flex: 1 1 auto;
  min-width: 0;
}

.linkInfoWorkout__zoneTitle {
  margin: 0 0 4px;
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
  line-height: 1.4;
  color: var(--link-ink);
}

.linkInfoWorkout__zoneDesc {
  margin: 0;
  font-size: var(--link-text-xs, 12px);
  color: var(--link-text-muted, #6b6560);
  line-height: 1.45;
}

.linkInfoWorkout__zoneCount {
  flex: 0 0 auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line-strong, #e5dfd6);
  font-size: 11px;
  color: var(--link-ink-secondary);
}

.linkInfoWorkout__zoneBody {
  padding: 16px;
}

.linkInfoWorkout__bindingList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.linkInfoWorkout__zoneEmpty {
  padding: 20px 16px;
  border: 1px dashed var(--link-line-strong, #d6d3d1);
  border-radius: 10px;
  background: var(--link-surface-muted, #f7f4ef);
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  text-align: center;
  line-height: 1.5;
}

.linkInfoWorkout__label {
  display: inline-flex;
  align-self: flex-start;
  margin: 0;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: var(--link-font-semibold);
  line-height: 1.4;
  letter-spacing: 0.01em;
  color: var(--link-ink-muted);
  background: var(--link-surface-muted, #f0ebe3);
  border: 1px solid var(--link-line-hairline, rgba(229, 223, 214, 0.9));
}

.linkInfoWorkout__value {
  margin: 0;
  padding-left: 1.25rem;
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  line-height: var(--link-leading-normal);
  color: var(--link-ink);
  white-space: pre-wrap;
  word-break: break-word;
}

.linkInfoWorkout__value--warn {
  color: #c45656;
}

.linkInfoWorkout__hint {
  margin: 4px 0 0 1.25rem;
  font-size: var(--link-text-xs);
  color: var(--link-ink-muted);
  line-height: 1.45;
}

.linkInfoWorkout__bindingCard {
  padding: 12px 14px;
  border: 1px solid var(--link-line, #eee9e2);
  border-radius: 10px;
  background: var(--link-card-surface, #fff);
  box-shadow: 0 1px 2px rgba(28, 25, 23, 0.03);
}

.linkInfoWorkout__bindingTitle {
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
  margin-bottom: 10px;
}

.linkInfoWorkout__bindingOrder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  margin-right: 8px;
  padding: 0 6px;
  border-radius: 6px;
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line, #eee9e2);
  font-size: 11px;
  font-weight: var(--link-font-semibold);
  color: var(--link-ink-secondary);
  vertical-align: middle;
}

/* binding 卡片内字段横排，与上方 strip 区三列/四列对齐 */
.linkInfoWorkout__bindingGrid {
  display: grid;
  gap: 12px 20px;
  margin: 0;
}

.linkInfoWorkout__bindingGrid--actions {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.linkInfoWorkout__bindingGrid--music,
.linkInfoWorkout__bindingGrid--voices {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.linkInfoWorkout__bindingGrid--config {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--link-line, #eee9e2);
}

.linkInfoWorkout__bindingCell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  text-align: center;
}

.linkInfoWorkout__bindingCell .linkInfoWorkout__label {
  align-self: center;
}

.linkInfoWorkout__bindingMetaVal {
  margin: 0;
  padding-left: 0;
  width: 100%;
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  line-height: var(--link-leading-normal);
  color: var(--link-ink);
  text-align: center;
  word-break: break-word;
}

.linkInfoWorkout__bindingMetaVal--warn {
  color: #c45656;
  font-weight: var(--link-font-semibold);
}

.linkInfoWorkout__value--muted {
  font-weight: var(--link-font-regular);
  color: var(--link-ink-muted);
}

@media (max-width: 720px) {
  .linkInfoWorkout__meta--action,
  .linkInfoWorkout__meta--voice,
  .linkInfoWorkout__meta--music,
  .linkInfoWorkout__meta--nameLoop,
  .linkInfoWorkout__meta--collection {
    grid-template-columns: 1fr;
  }

  .linkInfoWorkout__bindingGrid--actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .linkInfoWorkout__bindingGrid--music,
  .linkInfoWorkout__bindingGrid--voices {
    grid-template-columns: 1fr;
  }
}
</style>
